import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import useReduxSelector from '@/Framework/hooks/useReduxSelector';
import { useRouter } from '@/Framework/Router';
import { useBreadcrumbsContext } from '@/finsight/ui/common/components/header/Breadcrumbs';
import { initProfilePage, resetProfilePage } from '@/finsight/application/actions/profile/actions';
import TogglingFilters from '@/finsight/ui/components/profile/TogglingFilters';
import PageHeaderWrapper from '@/finsight/ui/common/components/PageHeaderWrapper';
import IssueTile from '@/finsight/ui/components/deals/DealTiles/IssueTile';
import TopCompanies from '@/finsight/ui/common/components/TopCompanies';
import MarketChart from '@/finsight/ui/common/components/MarketChart';
import { ProfileTypes } from '@/finsight/application/actions/profile/constants';
import MissingSomethingModalButton from '@/finsight/ui/common/components/MissingSomethingModalButton';
import { usePageFiltersContext } from '@/finsight/application/PageFilters/PageFiltersContext';
import ResearchTile from '@/finsight/ui/components/research/components/ResearchTile';
import MetaSchema from '@/finsight/ui/common/components/MetaSchema';
import BondsTile from '@/finsight/ui/components/bonds/BondsTile';
import TraceTile from '@/finsight/ui/components/trace/TraceTile';
import CookiesBanner from '@/Framework/UI/Molecules/CookiesBanner';
import ArticlesTiles from '@/finsight/ui/components/creditFlow/articles/ArticlesTiles';
import getProfileOverviewSchema from './getProfileMetaSchema';
import getProfileBreadcrumbs from './getProfileBreadcrumbs';
import { useDictionariesContext } from '@/finsight/application/Dictionaries/DictionariesContext';
import { useUserPlanContext } from '@/finsight/application/User/UserPlan/UserPlanContext';
import { UserPlanType } from '@/users/domain/UserPlan';

import styles from './profile.scss';

interface IProps {
  profileType?: ProfileTypes,
  isConsumerOverview?: boolean,
}

export const stateSelector = (state) => ({
  pageFilters: state.pageFilters,
});

const Profile = ({ profileType, isConsumerOverview = false }: IProps) => {
  const {
    isHeaderInitialized,
    productsList: products,
    regionsList: regions,
    industriesList,
    sectorsList,
    mapping,
  } = useDictionariesContext();
  const [pageHeader, setPageHeader] = useState<string>('');
  const { pathname, query } = useRouter();
  const { industry, sector }: any = query;
  const dispatch = useDispatch();
  const { pageFilters } = useReduxSelector(stateSelector);
  const { setBreadcrumbs } = useBreadcrumbsContext();
  const { accessibleProducts, accessibleRegions } = usePageFiltersContext();

  const { checkAccessByUserPlan } = useUserPlanContext();
  const { checkAccessByUserRegions, checkAccessByUserProducts } = usePageFiltersContext();
  const userAccessChecks = [
    checkAccessByUserRegions(),
    checkAccessByUserProducts(),
  ];
  const userAccessChecksWithPro = [
    ...userAccessChecks,
    checkAccessByUserPlan(UserPlanType.PRO),
  ];
  const userAccessChecksWithPremium = [
    ...userAccessChecks,
    checkAccessByUserPlan(UserPlanType.PREMIUM),
  ];

  const initPageTitle = (pageHeader, pageNameSuffix: string = '') => {
    // if we're on MarketOverview and product is ABS
    if (profileType === ProfileTypes.OVERVIEW && pageFilters.sectorBlock) {
      pageNameSuffix = isConsumerOverview ? ' (ex CLO/CMBS/RMBS)' : ': All';
    }

    return pageHeader + pageNameSuffix;
  };

  const pageTitle = initPageTitle(pageHeader);

  const init = (skipNextUpdate: boolean = false) => {
    const headerData = {
      products,
      regions,
      mapping,
      industriesList,
      sectorsList,
    };
    dispatch(initProfilePage({
        skipNextUpdate,
        profileType,
        isConsumerOverview,
        query,
        headerData,
    }));
  };

  const updatePageHeaderAndTileText = (pageHeader: string = 'Market Overview') => {
    // we're on industry/subindustry profile page
    if (industry && profileType === ProfileTypes.INDUSTRY) {
      const { pageTitleName = '' } = industriesList[industry] || {};
      pageHeader = pageTitleName;
    }

    // we're on sector/subsector profile page
    if (sector && profileType === ProfileTypes.SECTOR) {
      const { pageTitleName = '' } = sectorsList[sector] || {};
      pageHeader = `ABS ${ pageHeader } ${ pageTitleName }`;
    }

    setPageHeader(pageHeader);
  };

  const updateBreadcrumbs = () => {
    if (profileType === ProfileTypes.OVERVIEW) {
      const breadcrumbs = getProfileBreadcrumbs({
        profileType,
      }, query);

      setBreadcrumbs(breadcrumbs);
      return;
    }

    // we're on industry/subindustry profile page
    if (profileType === ProfileTypes.INDUSTRY && industriesList[industry]) {
      const { industryName, subindustryName } = industriesList[industry];
      const breadcrumbs = getProfileBreadcrumbs({
        profileType,
        itemName: industryName,
        subitemName: subindustryName,
      }, query);

      setBreadcrumbs(breadcrumbs);
      return;
    }

    // we're on sector/subsector profile page
    if (profileType === ProfileTypes.SECTOR && sectorsList[sector]) {
      const { sectorName, subsectorName } = sectorsList[sector];
      const breadcrumbs = getProfileBreadcrumbs({
        profileType,
        itemName: sectorName,
        subitemName: subsectorName,
      }, query);

      setBreadcrumbs(breadcrumbs);
    }
  };

  const getSchema = () => {
    if (profileType === ProfileTypes.OVERVIEW) {
      return getProfileOverviewSchema(pathname);
    }
    return null;
  };

  useEffect(() => {
    if (pageFilters.skipNextUpdate) {
      // re-init, skip next props update
      init(true);
    }

    updatePageHeaderAndTileText();
    updateBreadcrumbs();
  }, [isHeaderInitialized, pageFilters, industry, sector]);

  useEffect(() => {
    updatePageHeaderAndTileText();
    updateBreadcrumbs();
    if (isHeaderInitialized) {
      init();
    }

    return () => {
      dispatch(resetProfilePage());
      setBreadcrumbs(null);
    };
  }, [isHeaderInitialized, query]);

  return (
    <>
      <MetaSchema schema={ getSchema() } />
      <div className={ styles.profileLayout }>
        <PageHeaderWrapper title={ pageTitle }>
          <TogglingFilters
            filterSet={ [
                {
                  key: 'products',
                  items: accessibleProducts,
                  isVisible: !pageFilters.sector && !pageFilters.isConsumerOverview,
                },
                {
                  key: 'regions',
                  items: accessibleRegions,
                },
              ] }
          />
        </PageHeaderWrapper>

        <div className={ styles.topParentsAndChartWrapper }>
          <div className={ styles.topParentsWrapper }>
            <TopCompanies />
          </div>
          <div className={ styles.chartWrapper }>
            <MarketChart />
          </div>
        </div>
        <ArticlesTiles userAccessChecks={ userAccessChecksWithPremium } />
        <IssueTile profileType={ profileType } defaultExpandedRowIndexes={ [0] } />
        <BondsTile userAccessChecks={ userAccessChecksWithPro } />
        <TraceTile userAccessChecks={ userAccessChecksWithPro } />
        <ResearchTile />
      </div>
      <MissingSomethingModalButton />
      { profileType === ProfileTypes.OVERVIEW && (
        <CookiesBanner />
      ) }
    </>
  );
};

export default Profile;
